var _State_instances, _State_matchTarget, _State_runProseNode, _State_runProseMark, _State_runNode;
import { __classPrivateFieldGet } from "tslib";
/* Copyright 2021, Milkdown by Mirone. */
import { serializerMatchError } from '@milkdown/exception';
const isFragment = (x) => Object.prototype.hasOwnProperty.call(x, 'size');
/**
 * State for serializer.
 * Transform prosemirror state into remark AST.
 */
export class State {
    constructor(stack, schema, specMap) {
        this.stack = stack;
        this.schema = schema;
        this.specMap = specMap;
        _State_instances.add(this);
        /**
         * Use a remark parser to serialize current AST stored.
         *
         * @param remark - The remark parser needs to used.
         * @returns Result markdown string.
         */
        this.toString = (remark) => remark.stringify(this.stack.build());
        /**
         * Give the node or node list back to the state and the state will find a proper runner (by `match` method) to handle it.
         *
         * @param nodes - The node or node list needs to be handled.
         *
         * @returns The state instance.
         */
        this.next = (nodes) => {
            if (isFragment(nodes)) {
                nodes.forEach((node) => {
                    __classPrivateFieldGet(this, _State_instances, "m", _State_runNode).call(this, node);
                });
                return this;
            }
            __classPrivateFieldGet(this, _State_instances, "m", _State_runNode).call(this, nodes);
            return this;
        };
        /**
         * Add a node without open or close it.
         *
         * @remarks
         * It's useful for nodes which don't have content.
         *
         * @param type - Type of this node.
         * @param children - Children of this node.
         * @param value - Value of this node.
         * @param props - Additional props of this node.
         *
         * @returns The added node.
         */
        this.addNode = (...args) => {
            this.stack.addNode(...args);
            return this;
        };
        /**
         * Open a node, and all nodes created after this method will be set as the children of the node until a `closeNode` been called.
         *
         * @remarks
         * You can imagine `openNode` as the left half of parenthesis and `closeNode` as the right half. For nodes have children, your runner should just take care of the node itself and let other runners to handle the children.
         *
         * @param type - Type of this node.
         * @param value - Value of this node.
         * @param props - Additional props of this node.
         *
         * @returns The state instance.
         */
        this.openNode = (...args) => {
            this.stack.openNode(...args);
            return this;
        };
        /**
         * Close current node.
         *
         * @returns The node closed.
         */
        this.closeNode = (...args) => {
            this.stack.closeNode(...args);
            return this;
        };
        /**
         * Used when current node has marks, the serializer will auto combine marks nearby.
         *
         * @param mark - The mark need to be opened.
         * @param type - Type of this mark.
         * @param value - Value of this mark.
         * @param props - Additional props of this mark.
         *
         * @returns The state instance.
         */
        this.withMark = (...args) => {
            this.stack.openMark(...args);
            return this;
        };
    }
    /**
     * Transform a prosemirror node tree into remark AST.
     *
     * @param tree - The prosemirror node tree needs to be transformed.
     *
     * @returns The state instance.
     */
    run(tree) {
        this.next(tree);
        return this;
    }
}
_State_instances = new WeakSet(), _State_matchTarget = function _State_matchTarget(node) {
    const result = Object.entries(this.specMap)
        .map(([key, spec]) => (Object.assign({ key }, spec)))
        .find((x) => x.match(node));
    if (!result)
        throw serializerMatchError(node.type);
    return result;
}, _State_runProseNode = function _State_runProseNode(node) {
    const { runner } = __classPrivateFieldGet(this, _State_instances, "m", _State_matchTarget).call(this, node);
    runner(this, node);
}, _State_runProseMark = function _State_runProseMark(mark, node) {
    const { runner } = __classPrivateFieldGet(this, _State_instances, "m", _State_matchTarget).call(this, mark);
    return runner(this, mark, node);
}, _State_runNode = function _State_runNode(node) {
    const { marks } = node;
    const unPreventNext = marks.every((mark) => !__classPrivateFieldGet(this, _State_instances, "m", _State_runProseMark).call(this, mark, node));
    if (unPreventNext) {
        __classPrivateFieldGet(this, _State_instances, "m", _State_runProseNode).call(this, node);
    }
    marks.forEach((mark) => this.stack.closeMark(mark));
};
//# sourceMappingURL=state.js.map